import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { Trash2, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658'];

function App() {
    const [summary, setSummary] = useState({ balance: 0, income: 0, expense: 0, incomesByCategory: [], expensesByCategory: [], byMonth: [] });
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('auth') === 'true');
    const [loginData, setLoginData] = useState({ user: '', pass: '' });
    const [loginError, setLoginError] = useState('');
    const [newTransaction, setNewTransaction] = useState({ type: 'Despesa', amount: '', description: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const handleLogin = (e) => {
        e.preventDefault();
        if (loginData.user === 'solutionsdigital' && loginData.pass === 'Padoka13@#$') {
            setIsAuthenticated(true);
            localStorage.setItem('auth', 'true');
            setLoginError('');
        } else {
            setLoginError('Usuário ou senha incorretos.');
        }
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        localStorage.removeItem('auth');
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const [summaryRes, transRes] = await Promise.all([
                axios.get('/api/summary'),
                axios.get('/api/transactions')
            ]);
            setSummary(summaryRes.data);
            setTransactions(transRes.data);
        } catch (error) {
            console.error("Erro ao buscar dados da API:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchData();
        }
    }, [isAuthenticated]);

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta transação?')) {
            try {
                await axios.delete(`/api/transactions/${id}`);
                fetchData(); // Recarrega os dados após a exclusão
            } catch (error) {
                console.error("Erro ao deletar transação:", error);
                alert("Erro ao excluir transação.");
            }
        }
    };

    const handleCreateTransaction = async (e) => {
        e.preventDefault();

        if (!newTransaction.amount || !newTransaction.description) {
            alert('Preencha o valor e a descrição.');
            return;
        }

        try {
            setIsSaving(true);

            if (editingId) {
                await axios.put(`/api/transactions/${editingId}`, {
                    type: newTransaction.type,
                    amount: newTransaction.amount,
                    description: newTransaction.description
                });
            } else {
                await axios.post('/api/transactions', {
                    type: newTransaction.type,
                    amount: newTransaction.amount,
                    description: newTransaction.description
                });
            }

            // Limpa o form e recarrega os dados
            setNewTransaction({ type: 'Despesa', amount: '', description: '' });
            setEditingId(null);
            fetchData();
        } catch (err) {
            console.error("Erro ao salvar transação", err);
            alert("Erro ao salvar a transação. Verifique os dados inseridos.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditClick = (t) => {
        setNewTransaction({
            type: t.type,
            amount: t.amount,
            description: t.description
        });
        setEditingId(t.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setNewTransaction({ type: 'Despesa', amount: '', description: '' });
        setEditingId(null);
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans p-6 rounded">
                <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 w-full max-w-sm">
                    <h2 className="text-2xl font-bold text-slate-800 text-center mb-6">Acesso ao Dashboard</h2>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Usuário</label>
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                value={loginData.user}
                                onChange={(e) => setLoginData({ ...loginData, user: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Senha</label>
                            <input
                                type="password"
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                value={loginData.pass}
                                onChange={(e) => setLoginData({ ...loginData, pass: e.target.value })}
                                required
                            />
                        </div>
                        {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
                        <button
                            type="submit"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg transition"
                        >
                            Entrar
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-800 text-xl font-semibold">Carregando dados...</div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-10 font-sans">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header */}
                <header className="flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-800">Gestão Financeira</h1>
                        <p className="text-slate-500 mt-1">Visão geral automatizada via Telegram</p>
                    </div>
                    <div className="mt-4 md:mt-0 flex gap-3">
                        <button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow transition">
                            Atualizar Dados
                        </button>
                        <button onClick={handleLogout} className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg shadow transition">
                            Sair
                        </button>
                    </div>
                </header>

                {/* Resumo (Cards) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Saldo Atual</p>
                            <h2 className="text-3xl font-bold text-slate-800 mt-2">
                                R$ {summary.balance.toFixed(2)}
                            </h2>
                        </div>
                        <div className={`p-4 rounded-full ${summary.balance >= 0 ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'}`}>
                            <DollarSign size={28} />
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Receitas</p>
                            <h2 className="text-3xl font-bold text-emerald-600 mt-2">
                                R$ {summary.income.toFixed(2)}
                            </h2>
                        </div>
                        <div className="p-4 rounded-full bg-emerald-100 text-emerald-600">
                            <TrendingUp size={28} />
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Despesas</p>
                            <h2 className="text-3xl font-bold text-orange-600 mt-2">
                                R$ {summary.expense.toFixed(2)}
                            </h2>
                        </div>
                        <div className="p-4 rounded-full bg-orange-100 text-orange-600">
                            <TrendingDown size={28} />
                        </div>
                    </div>
                </div>

                {/* Formulário de Nova Transação / Edição */}
                <div className={`rounded-xl shadow-sm border p-6 mb-8 transition-colors ${editingId ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-slate-100'}`}>
                    <h3 className="text-lg font-bold text-slate-800 mb-4">
                        {editingId ? 'Editar Lançamento' : 'Adicionar Lançamento Manual'}
                    </h3>
                    <form onSubmit={handleCreateTransaction} className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="w-full md:w-1/4">
                            <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                            <select
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                                value={newTransaction.type}
                                onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value })}
                            >
                                <option value="Despesa">Despesa / Pagamento</option>
                                <option value="Receita">Receita / Entrada</option>
                            </select>
                        </div>
                        <div className="w-full md:w-1/4">
                            <label className="block text-sm font-medium text-slate-700 mb-1">Valor (R$)</label>
                            <input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                value={newTransaction.amount}
                                onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                                required
                            />
                        </div>
                        <div className="w-full md:w-2/4">
                            <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
                            <input
                                type="text"
                                placeholder="Ex: mercado, uber, salario..."
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                value={newTransaction.description}
                                onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                                required
                            />
                        </div>
                        <div className="w-full md:w-auto mt-4 md:mt-0 flex gap-2">
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`w-full md:w-auto px-6 py-2 rounded-lg font-semibold text-white transition ${isSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                            >
                                {isSaving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Adicionar')}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg font-semibold text-slate-700 transition"
                                >
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* Gráficos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* Gráfico de Saídas por Categoria */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                        <h3 className="text-lg font-bold text-slate-800 mb-6">Saídas por Categoria</h3>
                        <div className="h-72">
                            {summary.expensesByCategory.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={summary.expensesByCategory}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={5}
                                            dataKey="value"
                                            nameKey="category"
                                            label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {summary.expensesByCategory.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip formatter={(value) => `R$ ${value.toFixed(2)}`} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-slate-400">Nenhuma saída registrada.</div>
                            )}
                        </div>
                    </div>

                    {/* Gráfico de Entradas por Fonte */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
                        <h3 className="text-lg font-bold text-slate-800 mb-6">Entradas por Origem</h3>
                        <div className="h-72">
                            {summary.incomesByCategory.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={summary.incomesByCategory}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={5}
                                            dataKey="value"
                                            nameKey="category"
                                            label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {summary.incomesByCategory.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={['#10b981', '#34d399', '#059669', '#6ee7b7', '#a7f3d0'][index % 5]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip formatter={(value) => `R$ ${value.toFixed(2)}`} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-slate-400">Nenhuma entrada registrada.</div>
                            )}
                        </div>
                    </div>

                    {/* Gráfico Comparativo Mensal (Largo) */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100 lg:col-span-2">
                        <h3 className="text-lg font-bold text-slate-800 mb-6">Comparativo Mensal</h3>
                        <div className="h-72">
                            {summary.byMonth.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={summary.byMonth} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <RechartsTooltip formatter={(value) => `R$ ${value.toFixed(2)}`} />
                                        <Legend />
                                        <Bar dataKey="incomes" name="Receitas" fill="#059669" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="expenses" name="Despesas" fill="#ea580c" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-slate-400">Dados insuficientes para o comparativo.</div>
                            )}
                        </div>
                    </div>

                </div>

                {/* Tabela de Últimas Transações */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="p-6 border-b border-slate-100">
                        <h3 className="text-lg font-bold text-slate-800">Últimas Transações</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 text-slate-500 text-sm uppercase tracking-wider">
                                    <th className="px-6 py-4 font-medium">Data</th>
                                    <th className="px-6 py-4 font-medium">Descrição</th>
                                    <th className="px-6 py-4 font-medium">Categoria</th>
                                    <th className="px-6 py-4 font-medium text-right">Valor</th>
                                    <th className="px-6 py-4 font-medium text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-700">
                                {transactions.length > 0 ? (
                                    transactions.slice(0, 15).map((t) => (
                                        <tr key={t.id} className="hover:bg-slate-50/50 transition">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                                {new Date(t.date).toLocaleDateString('pt-BR')}
                                            </td>
                                            <td className="px-6 py-4 font-medium">
                                                {t.description}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-500">
                                                <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs font-medium">
                                                    {t.category}
                                                </span>
                                            </td>
                                            <td className={`px-6 py-4 text-right font-bold whitespace-nowrap ${t.type === 'Receita' ? 'text-emerald-600' : 'text-slate-800'}`}>
                                                {t.type === 'Receita' ? '+' : '-'} R$ {t.amount.toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEditClick(t)}
                                                        className="text-slate-400 hover:text-blue-500 transition p-1 rounded-md hover:bg-blue-50"
                                                        title="Editar"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(t.id)}
                                                        className="text-slate-400 hover:text-red-500 transition p-1 rounded-md hover:bg-red-50"
                                                        title="Excluir"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-slate-500 font-medium">
                                            Nenhuma transação encontrada. Comece enviando mensagens para o Bot!
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default App;
